import React, { useEffect } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Image, Text } from '@chakra-ui/react';

import nothingHere from '../../../../../../nothing-here.png';
import colors from '../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../types/RequirementTemplateBlueprintType';
import { get } from '../../../../../../utils/arrayUtil';
import { REQUIREMENT_TYPES } from '../../../../util/options';

import InsuranceDocumentTemplateBlock from './components/InsuranceDocumentTemplateBlock';
import InsuranceLanguageTemplateBlock from './components/InsuranceLanguageTemplateBlock';
import InsurancePolicyTemplateBlock from './components/InsurancePolicyTemplateBlock';

function RequirementTemplateBlocks({ blueprint }) {
  const dispatch = useDispatch();
  const requirementTemplateBlocks = useSelector(blueprint.selectCurrentRequirementTemplateBlocks);

  useEffect(() => {
    dispatch(blueprint.deleteEmptyBlocks());
  }, []);

  const onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index !== result.source.index) {
      const currentTemplateBlock = requirementTemplateBlocks.find(block => block.order === result.source.index);
      dispatch(blueprint.updateRequirementTemplateBlock(currentTemplateBlock.id, {
        ...currentTemplateBlock,
        order: result.destination.index
      }));
    }
  };

  const mapBlocks = requirementTemplateBlock => {
    const requirementType = get(
      requirementTemplateBlock.requirementTemplateBlockProperties,
      '0.requirementType'
    ) || REQUIREMENT_TYPES.Policies;

    switch (requirementType) {
      case REQUIREMENT_TYPES.Policies:
        return (
          <Draggable
            draggableId={`${requirementTemplateBlock.id}`}
            index={requirementTemplateBlock.order}
            key={requirementTemplateBlock.id}
          >
            {provided => (
              <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                <InsurancePolicyTemplateBlock
                  blueprint={blueprint}
                  templateBlock={requirementTemplateBlock}
                />
              </Box>
            )}
          </Draggable>
        );

      case REQUIREMENT_TYPES.Documents:
        return (
          <Draggable
            draggableId={`${requirementTemplateBlock.id}`}
            index={requirementTemplateBlock.order}
            key={requirementTemplateBlock.id}
          >
            {provided => (
              <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                <InsuranceDocumentTemplateBlock
                  blueprint={blueprint}
                  templateBlock={requirementTemplateBlock}
                />
              </Box>
            )}
          </Draggable>
        );

      case REQUIREMENT_TYPES.Language:
        return (
          <Draggable
            draggableId={`${requirementTemplateBlock.id}`}
            index={requirementTemplateBlock.order}
            key={requirementTemplateBlock.id}
          >
            {provided => (
              <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                <InsuranceLanguageTemplateBlock
                  blueprint={blueprint}
                  templateBlock={requirementTemplateBlock}
                />
              </Box>
            )}
          </Draggable>
        );
      default:
        return null;
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="templateBlockList">
        {provided => (
          <Box
            {...provided.droppableProps}
            borderTop={`.375rem solid ${colors.greyScale['200']}`}
            height="100%"
            justifyContent="center"
            layerStyle="partialPanelBottom"
            minH="70vh"
            minW="480px"
            mr={4}
            overflowY="auto"
            p={6}
            ref={provided.innerRef}
            w="100%"
          >
            {(requirementTemplateBlocks.length > 0) && (
              <>
                {requirementTemplateBlocks.map(mapBlocks)}
                {provided.placeholder}
                <Text
                  color={colors.greyScale['400']}
                  justifyContent="center"
                  textStyle="bodySm500"
                  w="100%"
                >
                  -  Click Next Requirement to Add. Drag to Change Order. Click Blocks to Edit.  -
                </Text>
              </>
            )}
            {!requirementTemplateBlocks.length && (
              <Box alignItems="center" justifyItems="center" mt={20}>
                <Image margin="auto" src={nothingHere} />
                <Text color={colors.greyScale['500']} mt={4} textStyle="bodyMd500">
                  Nothing Here
                </Text>
                <Text color={colors.greyScale['400']} textStyle="bodySm500">
                  Click on your first requirement to get started.
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}

RequirementTemplateBlocks.propTypes = { blueprint: RequirementTemplateBlueprintType };

export default RequirementTemplateBlocks;
